import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import './JsonLdEditor.css';
import { fetchSchemaOrgTypes, SchemaOrgType } from './rest';
import { createPortal } from 'react-dom';
import Summary from './components/Summary';
import Footer from './common/Footer';

import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import { createDefaultConfig } from './common/cookieConsentConfigs';

// Type definitions
type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];
type Path = (string | number)[];

// Context for JSON-LD data
interface JsonLdContextType {
  jsonData: JsonObject;
  updateJsonData: (newData: JsonObject) => void;
  getValueAtPath: (path: Path) => JsonValue | undefined;
  setValueAtPath: (path: Path, value: JsonValue) => void;
  deleteAtPath: (path: Path) => void;
  renameKey: (path: Path, newKey: string) => void;
  addProperty: (path: Path, key: string, value: JsonValue) => void;
  addArrayItem: (path: Path, value: JsonValue) => void;
  convertValueType: (path: Path, newType: 'string' | 'object' | 'array') => void;
}

const JsonLdContext = createContext<JsonLdContextType | undefined>(undefined);

// Hook to use the JsonLd context
function useJsonLd() {
  const context = useContext(JsonLdContext);
  if (!context) {
    throw new Error('useJsonLd must be used within a JsonLdProvider');
  }
  return context;
}

// Utility functions for REST API

// Utility functions for traversing JSON-LD
function findNearestContext(obj: JsonObject, path: Path): string {
  let current = obj;
  let context = obj['@context'] as string || 'http://schema.org';

  // Walk up the path looking for context
  for (let i = 0; i < path.length; i++) {
    const segment = path[i];
    if (typeof current !== 'object' || current === null) break;

    if (Array.isArray(current)) {
      if (typeof segment === 'number' && current[segment]) {
        current = current[segment] as JsonObject;
        if (current && typeof current === 'object' && !Array.isArray(current) && '@context' in current) {
          context = current['@context'] as string;
        }
      }
    } else {
      if (typeof segment === 'string' && segment in current) {
        current = current[segment] as JsonObject;
        if (current && typeof current === 'object' && !Array.isArray(current) && '@context' in current) {
          context = current['@context'] as string;
        }
      }
    }
  }

  return context;
}

// Helper function to generate unique IDs
function generateNodeId(): string {
  return `node-${Math.random().toString(36).substring(2, 11)}`;
}

// Helper function to generate unique keys
function generateUniqueKey(obj: JsonObject, baseKey: string): string {
  let key = baseKey;
  let counter = 1;

  while (obj.hasOwnProperty(key)) {
    key = `${baseKey}${counter}`;
    counter++;
  }

  return key;
}

interface JsonLdProviderProps {
  initialData?: JsonObject;
  children: React.ReactNode;
}

export function JsonLdProvider({ initialData, children }: JsonLdProviderProps) {
  const [jsonData, setJsonData] = useState<JsonObject>(initialData || {
    "@context": "http://schema.org",
    "@type": "WebApplication",
    "name": "VimDiff Color Config Generator",
    "author": {
      // "@type": "Organization",
      "name": "Debug In The Woods",
      "url": "https://apps.debuginthewoods.net/"
    }
  });

  // Function to get a value at a specific path in the JSON
  function getValueAtPath(path: Path): JsonValue | undefined {
    if (!path || path.length === 0) return jsonData;

    let current: any = jsonData;
    for (const key of path) {
      if (current === undefined || current === null) return undefined;
      current = current[key];
    }
    return current;
  }

  // Function to set a value at a specific path in the JSON
  function setValueAtPath(path: Path, value: JsonValue) {
    if (!path || path.length === 0) return;

    setJsonData(prevData => {
      const newData = { ...prevData };
      let current = newData;

      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (current[key as keyof typeof current] === undefined) {
          // Create the path if it doesn't exist
          if (typeof path[i + 1] === 'number') {
            current[key as keyof typeof current] = [] as unknown as JsonValue;
          } else {
            current[key as keyof typeof current] = {} as JsonValue;
          }
        }
        current = current[key as keyof typeof current] as JsonObject;
      }

      const lastKey = path[path.length - 1];
      if (Array.isArray(current)) {
        current[lastKey as number] = value;
      } else {
        current[lastKey as string] = value;
      }

      return newData;
    });
  }

  // Function to delete a value at a specific path in the JSON
  function deleteAtPath(path: Path) {
    if (!path || path.length === 0) return;

    setJsonData(prevData => {
      const newData = { ...prevData };

      // If we're deleting a root property
      if (path.length === 1) {
        delete newData[path[0] as string];
        return newData;
      }

      let current: any = newData;
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (current === undefined || current === null) return prevData;
        current = current[key];
      }

      const lastKey = path[path.length - 1];
      if (Array.isArray(current)) {
        current.splice(lastKey as number, 1);
      } else {
        delete current[lastKey as string];
      }

      return newData;
    });
  }

  // Function to rename a key in an object
  function renameKey(path: Path, newKey: string) {
    if (!path || path.length === 0 || !newKey) return;

    setJsonData(prevData => {
      const newData = { ...prevData };

      // Get the parent object
      const parentPath = path.slice(0, -1);
      const oldKey = path[path.length - 1] as string;

      let parent = newData;
      for (const segment of parentPath) {
        parent = parent[segment as keyof typeof parent] as JsonObject;
      }

      if (!parent || typeof parent !== 'object' || Array.isArray(parent)) return prevData;

      // Check if the new key already exists
      if (parent.hasOwnProperty(newKey)) return prevData;

      // Copy value to new key and delete old key
      parent[newKey] = parent[oldKey];
      delete parent[oldKey];

      return newData;
    });
  }

  // Function to add a new property to an object
  function addProperty(path: Path, key: string, value: JsonValue) {
    if (!path || !key) return;

    setJsonData(prevData => {
      const newData = { ...prevData };
      const target = getValueAtPath(path);

      if (target && typeof target === 'object' && !Array.isArray(target)) {
        const updatedTarget = { ...(target as JsonObject), [key]: value };
        setValueAtPath(path, updatedTarget);
      }

      return newData;
    });
  }

  // Function to add an item to an array
  function addArrayItem(path: Path, value: JsonValue) {
    if (!path) return;

    setJsonData(prevData => {
      const newData = { ...prevData };
      const target = getValueAtPath(path);

      if (target && Array.isArray(target)) {
        const updatedArray = [...target, value];
        setValueAtPath(path, updatedArray);
      }

      return newData;
    });
  }

  // Function to convert a value to a different type
  function convertValueType(path: Path, newType: 'string' | 'object' | 'array') {
    if (!path || !newType) return;

    setJsonData(prevData => {
      const newData = { ...prevData };

      let newValue: JsonValue;
      switch (newType) {
        case 'string':
          newValue = '';
          break;
        case 'object':
          newValue = {};
          break;
        case 'array':
          newValue = [];
          break;
        default:
          return prevData;
      }

      setValueAtPath(path, newValue);
      return newData;
    });
  }

  // Update JSON data function for external components
  function updateJsonData(newData: JsonObject) {
    setJsonData(newData);
  }

  const contextValue: JsonLdContextType = {
    jsonData,
    updateJsonData,
    getValueAtPath,
    setValueAtPath,
    deleteAtPath,
    renameKey,
    addProperty,
    addArrayItem,
    convertValueType
  };

  return (
    <JsonLdContext.Provider value={contextValue}>
      {children}
    </JsonLdContext.Provider>
  );
}

// Track which context values we've already fetched types for
const calledContextValues = new Set<string>();

// Individual components for rendering different value types
interface ScalarValueProps {
  keyName: string;
  value: string | number | boolean;
  path: Path;
}

// Type-ahead datalist component
interface TypeAheadDatalistProps {
  dataListId: string | null;
  typeOptions: SchemaOrgType[];
}

function TypeAheadDatalist({ dataListId, typeOptions }: TypeAheadDatalistProps) {
  if (!dataListId || typeOptions.length === 0) return null;
  
  return createPortal(
    <datalist id={dataListId}>
      {typeOptions.map((option, index) => (
        <option key={index} value={option.label}>
          {option.description}
        </option>
      ))}
    </datalist>,
    document.body
  );
}

function ScalarValue({ keyName, value, path }: ScalarValueProps) {
  const { setValueAtPath, convertValueType, jsonData } = useJsonLd();
  const [dataListId, setDataListId] = useState<string | null>(null);
  const [typeOptions, setTypeOptions] = useState<SchemaOrgType[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValueAtPath(path, e.target.value);
  };

  useEffect(() => {
    // Set up type-ahead for @type values
    if (keyName === "@type" && inputRef.current) {
      const setupTypeAhead = async () => {
        const contextValue = findNearestContext(jsonData, path);
        const encodedContextValue = encodeURIComponent(contextValue);
        const listId = `list-${encodedContextValue}`;

        setDataListId(listId);

        // Only fetch if we haven't fetched types for this context yet
        if (!calledContextValues.has(contextValue)) {
          calledContextValues.add(contextValue);
          const types = await fetchSchemaOrgTypes(contextValue);
          if (types.length > 0) {
            setTypeOptions(types);
          }
        }
      };

      setupTypeAhead();
    }
  }, [keyName, path, jsonData]);

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        className="json-value"
        value={value.toString()}
        onChange={handleChange}
        list={dataListId || undefined}
      />
      <TypeAheadDatalist dataListId={dataListId} typeOptions={typeOptions} />
      <button onClick={() => convertValueType(path, 'object')}>to Object</button>
      <button onClick={() => convertValueType(path, 'array')}>to Array</button>
    </>
  );
}

interface ObjectValueProps {
  value: JsonObject;
  path: Path;
}

function ObjectValue({ value, path }: ObjectValueProps) {
  const { addProperty, convertValueType } = useJsonLd();

  const handleAddProperty = () => {
    const newKey = generateUniqueKey(value, 'newKey');
    addProperty(path, newKey, '');
  };

  return (
    <div className="json-value nested">
      {'{'}
      <div className="nested-props">
        {Object.entries(value).map(([key, val]) => (
          <JsonNode
            key={`${path.join('-')}-${key}`}
            keyName={key}
            value={val}
            path={[...path, key]}
          />
        ))}
      </div>
      {'}'}
      <button onClick={handleAddProperty}>Add Property</button>
      <button onClick={() => convertValueType(path, 'string')}>to String</button>
    </div>
  );
}

interface ArrayValueProps {
  value: JsonArray;
  path: Path;
}

function ArrayValue({ value, path }: ArrayValueProps) {
  const { addArrayItem, convertValueType } = useJsonLd();

  const handleAddString = () => addArrayItem(path, '');
  const handleAddObject = () => addArrayItem(path, {});
  const handleAddArray = () => addArrayItem(path, []);

  return (
    <div className="json-value nested">
      {'['}
      <div className="nested-items">
        {value.map((item, index) => (
          <JsonNode
            key={`${path.join('-')}-${index}`}
            keyName={index.toString()}
            value={item}
            path={[...path, index]}
            isArrayItem={true}
          />
        ))}
      </div>
      {']'}
      <div className="add-item-container">
        <span>Add: </span>
        <button onClick={handleAddString}>String</button>
        <button onClick={handleAddObject}>Object</button>
        <button onClick={handleAddArray}>Array</button>
      </div>
      <button onClick={() => convertValueType(path, 'string')}>to String</button>
    </div>
  );
}

interface JsonNodeProps {
  keyName: string;
  value: JsonValue;
  path: Path;
  isArrayItem?: boolean;
}

function JsonNode({ keyName, value, path, isArrayItem = false }: JsonNodeProps) {
  const { deleteAtPath, renameKey } = useJsonLd();
  const [key, setKey] = useState(keyName);
  const nodeId = useRef(generateNodeId());

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKey(e.target.value);
  };

  const handleKeyBlur = () => {
    if (key !== keyName) {
      renameKey(path, key);
    }
  };

  const handleDelete = () => {
    deleteAtPath(path);
  };

  return (
    <div className="json-row" id={nodeId.current} data-path={JSON.stringify(path)}>
      <span className="handle" draggable={true}>⋮</span>

      {!isArrayItem ? (
        <>
          <input
            type="text"
            className="json-key"
            value={key}
            onChange={handleKeyChange}
            onBlur={handleKeyBlur}
          />
          <span>: </span>
        </>
      ) : (
        <span>[{keyName}]: </span>
      )}

      <span className="actions">
        <button onClick={handleDelete}>Delete</button>
      </span>

      {value === null ? (
        <span className="json-value">null</span>
      ) : typeof value === 'object' && !Array.isArray(value) ? (
        <ObjectValue value={value} path={path} />
      ) : Array.isArray(value) ? (
        <ArrayValue value={value} path={path} />
      ) : (
        <ScalarValue keyName={keyName} value={value as string | number | boolean} path={path} />
      )}
    </div>
  );
}

// Main editor component
export function JsonLdEditor() {
  const { jsonData, updateJsonData } = useJsonLd();
  const [jsonOutput, setJsonOutput] = useState('');

  useEffect(() => {
    setJsonOutput(JSON.stringify(jsonData, null, 2));
  }, [jsonData]);

  const handleAddRootProperty = () => {
    const newData = { ...jsonData };
    const newKey = generateUniqueKey(newData, 'newKey');
    newData[newKey] = '';
    updateJsonData(newData);
  };

  const handleExportJson = () => {
    const jsonStr = JSON.stringify(jsonData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'exported.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="json-ld-editor">
      <div className="toolbar">
        <button id="add-root-property" onClick={handleAddRootProperty}>
          Add Root Property
        </button>
        <button id="export-json" onClick={handleExportJson}>
          Export JSON
        </button>
      </div>

      <div id="json-root" className="json-container">
        {Object.entries(jsonData).map(([key, value]) => (
          <JsonNode
            key={key}
            keyName={key}
            value={value}
            path={[key]}
          />
        ))}
      </div>

      <div className="json-output-container">
        <h3>JSON Output</h3>
        <pre id="json-output">{jsonOutput}</pre>
      </div>

      <div id="dataListContainer" style={{ display: 'none' }}></div>
    </div>
  );
}

// App component that wraps everything with the provider
export default function App() {
  useEffect(() => {
    // Load nginx CSS after base styles
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/cookieconsent-config.css';
    document.head.appendChild(link);

    // Apply the dark turquoise theme
    document.documentElement.classList.add('cc--dark-turquoise');
    
    // Initialize cookie consent
    CookieConsent.run(
      createDefaultConfig()
    );

    // Cleanup
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <JsonLdProvider>
      <div className="app">
        <h1>JSON-LD Editor</h1>
        <Summary />
        <JsonLdEditor />
        <Footer />
      </div>
    </JsonLdProvider>
  );
}